import { useEffect, useState } from 'react';
import { fetchSettings, updateSettings } from '@/api/settings';
import { PageHeader } from '@/components/ui/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { useMerchantStore } from '@/store/useMerchantStore';
import { resolveSettings } from '@/constants/defaults';
import { errorMessageOf } from '@/lib/errors';
import type { MerchantSettings } from '@/types';
import {
  GeneralSection,
  getBrandNameError,
  getSupportEmailError,
  getSupportPhoneError,
} from './settings/GeneralSection';
import { PolicySection } from './settings/PolicySection';
import { RefundModesSection } from './settings/RefundModesSection';
import { NotificationsSection } from './settings/NotificationsSection';
import {
  BrandingSection,
  getPortalTitleError,
  getPortalLogoUrlError,
  getPortalColorError,
} from './settings/BrandingSection';
import { ReasonsSection } from './settings/ReasonsSection';
import { WarehousesSection } from './settings/WarehousesSection';
import { TemplatesSection } from './settings/TemplatesSection';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'branding', label: 'Branding & go-live' },
  { id: 'policy', label: 'Policy' },
  { id: 'refunds', label: 'Refund modes' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'reasons', label: 'Reasons' },
  { id: 'warehouses', label: 'Warehouses' },
  { id: 'templates', label: 'Templates' },
];

const SETTINGS_TABS = new Set(['general', 'policy', 'refunds', 'notifications', 'branding']);

export function SettingsScreen() {
  const [activeTab, setActiveTab] = useState('general');
  const { data, errorMessage, isLoading, reload } = useAsyncData<MerchantSettings | null>(
    (signal) => fetchSettings(signal),
    [],
  );

  const [draft, setDraft] = useState<MerchantSettings | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setDraft(resolveSettings(data));
    }
  }, [data]);

  function applyPatch(patch: Partial<MerchantSettings>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  const generalSectionError = draft
    ? getBrandNameError(draft.customDomain ?? '') ??
      getSupportEmailError(draft.merchantNotifyEmail ?? '') ??
      getSupportPhoneError(draft.merchantPhone ?? '')
    : undefined;

  const brandingSectionError = draft
    ? getPortalTitleError(draft.portalTitle ?? '') ??
      getPortalLogoUrlError(draft.portalLogoUrl ?? '') ??
      getPortalColorError(draft.portalPrimaryColor ?? '#1A1A1A') ??
      getPortalColorError(draft.portalAccentColor ?? '#4F46E5')
    : undefined;

  const hasSettingsError = Boolean(generalSectionError || brandingSectionError);

  async function handleSave() {
    if (!draft) {
      return;
    }
    if (hasSettingsError) {
      toast.error(generalSectionError ?? brandingSectionError ?? 'Please fix the highlighted errors');
      return;
    }
    setIsSaving(true);
    try {
      await updateSettings(draft);
      useMerchantStore.getState().setMerchantName(draft.merchantName ?? '');
      toast.success('Settings saved');
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsSaving(false);
    }
  }

  const isSettingsTab = SETTINGS_TABS.has(activeTab);

  return (
    <div className="animate-fade-in pb-20">
      <PageHeader title="Settings" description="Configure your returns policy, branding, and automation." />

      <div className="mb-5">
        <Tabs tabs={TABS} activeId={activeTab} onChange={setActiveTab} />
      </div>

      {isSettingsTab ? (
        isLoading || !draft ? (
          <LoadingState message="Loading settings…" />
        ) : errorMessage ? (
          <ErrorState message={errorMessage} onRetry={reload} />
        ) : (
          <>
            {activeTab === 'general' ? <GeneralSection settings={draft} onChange={applyPatch} /> : null}
            {activeTab === 'policy' ? <PolicySection settings={draft} onChange={applyPatch} /> : null}
            {activeTab === 'refunds' ? <RefundModesSection settings={draft} onChange={applyPatch} /> : null}
            {activeTab === 'notifications' ? <NotificationsSection settings={draft} onChange={applyPatch} /> : null}
            {activeTab === 'branding' ? <BrandingSection settings={draft} onChange={applyPatch} /> : null}

            <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-line bg-surface/95 backdrop-blur lg:pl-60">
              <div className="mx-auto flex max-w-6xl items-center justify-end gap-3 px-4 py-3 lg:px-8">
                <span className="text-xs text-ink-subtle">Changes apply to the customer portal immediately.</span>
                <Button onClick={handleSave} isLoading={isSaving} disabled={hasSettingsError}>
                  Save changes
                </Button>
              </div>
            </div>
          </>
        )
      ) : null}

      {activeTab === 'reasons' ? <ReasonsSection /> : null}
      {activeTab === 'warehouses' ? <WarehousesSection /> : null}
      {activeTab === 'templates' ? <TemplatesSection /> : null}
    </div>
  );
}
